// Device VOD HLS URL assembly (design spec §8A.4 / §8A.2).
//
// The device serves playback media on a SEPARATE raw-HTTP listener (same-LAN,
// unauthenticated): GET /pb.m3u8?t1&t2 (VOD playlist), GET /seg/<seq>.ts (segment
// bytes, seq survives ring-wrap → evict 404), GET /snap/<ts_us>.jpg (snapshot).
// Segment URIs in the playlist are device-endpoint-RELATIVE, so the player resolves
// them against the playlist URL / device base.
//
// This is the single decision point for "where the device base URL comes from"
// (frontend plan Open Q-1). For the cloud-hosted https webapp, same-LAN direct
// http is blocked (mixed-content + CORS + NAT), so the REMOTE path tunnels these
// same endpoints over a WebRTC data channel (see webrtcPlayback.ts) — the URL
// shapes below are still the contract, just transported differently.

/** Where the device's playback endpoints live (same-LAN base, e.g. "http://192.168.1.20:8089"). */
export interface DeviceHlsBase {
  /** Origin + optional path prefix, no trailing slash (e.g. "http://192.168.1.20:8089"). */
  base: string;
}

function trimSlash(base: string): string {
  return base.replace(/\/+$/, '');
}

/** GET /pb.m3u8?t1=<us>&t2=<us> — the VOD playlist for [t1,t2]. */
export function pbPlaylistUrl(base: string, t1Us: number, t2Us: number): string {
  return `${trimSlash(base)}/pb.m3u8?t1=${t1Us}&t2=${t2Us}`;
}

/** GET /seg/<seq>.ts — segment bytes (seq = ts_ms, survives ring-wrap). */
export function pbSegmentUrl(base: string, seq: number | string): string {
  return `${trimSlash(base)}/seg/${seq}.ts`;
}

/** GET /snap/<ts_us>.jpg — sdcard snapshot bytes for a marker. */
export function pbSnapshotUrl(base: string, tsUs: number): string {
  return `${trimSlash(base)}/snap/${tsUs}.jpg`;
}

/**
 * The tunnel request a custom loader sends when media is delivered over the WebRTC
 * data channel (design spec §8A.3): a path like "/pb.m3u8?t1&t2" / "/seg/<seq>.ts" /
 * "/snap/<ts>.jpg" is parsed into {op, ...params} for the data-channel framing.
 */
export type TunnelRequest =
  | { op: 'm3u8'; t1: number; t2: number }
  | { op: 'seg'; seq: string }
  | { op: 'snap'; ts: number };

/**
 * Parse a device-endpoint-relative URL (absolute or relative) into a TunnelRequest.
 * Returns null when the path is not one of the three known endpoints. Pure +
 * unit-tested; this is the bridge between hls.js loader URLs and data-channel ops.
 */
export function parseTunnelRequest(url: string): TunnelRequest | null {
  // Resolve relative URLs against a dummy base so URL() can parse the query string.
  let u: URL;
  try {
    u = new URL(url, 'http://device.local');
  } catch {
    return null;
  }
  const path = u.pathname;
  if (path.endsWith('/pb.m3u8')) {
    const t1 = Number(u.searchParams.get('t1'));
    const t2 = Number(u.searchParams.get('t2'));
    if (!Number.isFinite(t1) || !Number.isFinite(t2)) return null;
    return { op: 'm3u8', t1, t2 };
  }
  const seg = path.match(/\/seg\/([^/]+)\.ts$/);
  if (seg) return { op: 'seg', seq: seg[1] };
  const snap = path.match(/\/snap\/([^/]+)\.jpg$/);
  if (snap) {
    const ts = Number(snap[1]);
    if (!Number.isFinite(ts)) return null;
    return { op: 'snap', ts };
  }
  return null;
}
